// Bilingual content for the two About pages (/about and /businesses/about).
// Rendered by pages/About.jsx and pages/AboutBusiness.jsx via MarketingPage.
//
// Rebuilt 2026-08-27 from the marketing-psychology briefs for both pages.
// Each export is { en: {hero, sections}, tl: {hero, sections} }: the page
// resolves the active language via useT() and passes content[lang].en as a
// fallback. No MySiteCare references: ConfirmPH stands on its own here.

export const ABOUT = {
  en: {
    hero: {
      kicker: "About ConfirmPH",
      title: "Rentals often start in chat. ConfirmPH gives the booking a clear record.",
      sub: "In the Philippines, many rentals begin through Messenger, Facebook, WhatsApp, Viber, phone calls, or walk-ins. That's normal. The problem starts when the important details stay scattered across those conversations. ConfirmPH helps the renter and rental business keep one clearer record of the vehicle, dates, price, pickup details, and confirmation after the conversation begins. ConfirmPH does not own or operate the rental businesses listed on the platform.",
    },
    sections: [
      { h: "Why ConfirmPH exists", body: [
        "A “yes, confirmed” message can still leave important questions unanswered.",
        "Which vehicle? The exact vehicle or clearly agreed category;",
        "Which dates? Pickup and return date and time;",
        "How much? Rental price, any advance or downpayment, any refundable security deposit, and the remaining rental balance;",
        "Where? Pickup location and important contact details.",
        "A clear booking should make those answers easy to find. A renter may have one version in Messenger. A staff member may have written something somewhere else. Another employee may never have seen the conversation. ConfirmPH exists so both sides have something clearer to reference.",
      ]},
      { h: "What ConfirmPH actually does", body: [
        "Record. The rental business records the vehicle, dates, service mode, price, any advance and security deposit, pickup, and other important booking details.",
        "Review. The renter sees what the business recorded and can confirm it or request a correction.",
        "Follow. Important booking status, changes, and relevant updates remain easier to reference as the rental moves forward: through readiness and handover, return and inspection, and any settlement until the rental is closed.",
        "Book normally. Confirm clearly.",
      ]},
      { h: "What does “Confirmed” actually mean?", body: [
        "Confirmed means the renter reviewed and accepted the particular booking version and material commercial terms presented at that time. If the business later changes a material term, the change does not inherit the earlier confirmation: the renter is asked to accept the new version, and the earlier one stays in the history.",
        "ConfirmPH can record what the business entered;",
        "what the customer reviewed;",
        "the customer's confirmation;",
        "relevant recorded changes and status.",
        "ConfirmPH does not guarantee that the vehicle will definitely be available;",
        "that the rental business will perform perfectly;",
        "vehicle condition, insurance coverage, or legal compliance;",
        "the business's or vehicle's legal readiness, or a driver's eligibility;",
        "who is at fault in an accident, or who is liable;",
        "refunds, or the return of or entitlement to a security deposit.",
        "ConfirmPH creates a clearer shared record. The rental business remains responsible for the rental itself.",
      ]},
      { h: "What can I do with ConfirmPH?", body: [
        "Already booked? Use Find my booking to see the booking information a participating rental business recorded for you.",
        "Booked somewhere else? Use Request confirmation to ask the rental business to create or share a ConfirmPH booking record, whether you booked through Messenger, Facebook, WhatsApp, Viber, phone, or in person.",
        "Still looking? Use Discover rentals to browse participating rental businesses and their listed vehicles.",
        "Want to prepare better? Use the free renter guides and safety checklist before payment, pickup, and return.",
        "Driving yourself? See which authorized drivers are on the booking, review any material change before accepting it, and follow the handover, return and post-rental status on the same record.",
      ]},
      { h: "Built for the way rentals happen here", body: [
        "You don't need to change how you contact the rental business.",
        "Messenger;",
        "Facebook;",
        "WhatsApp;",
        "Viber;",
        "phone;",
        "walk-in.",
        "Those can still be where the conversation starts. ConfirmPH adds structure after the conversation begins. This product understands how rentals work here: it doesn't ask everybody to stop using Messenger.",
      ]},
      { h: "Catch misunderstandings before pickup", body: [
        "It is easier to fix a misunderstanding before you travel.",
        "Wrong date?",
        "Different vehicle?",
        "Pickup location unclear?",
        "Price recorded differently?",
        "Advance or security deposit not what you expected?",
        "Those are easier to clarify before pickup than when you're already standing there. Before pickup, check ConfirmPH.",
      ]},
      { h: "What ConfirmPH is, and what it isn't", body: [
        "ConfirmPH is a software and information platform that helps rental businesses and renters create and reference clearer rental records.",
        "ConfirmPH is not the rental business itself. ConfirmPH does not own the vehicle, perform the rental, or guarantee the rental business's future performance.",
        "Your own checks still matter: inspect the vehicle;",
        "read the rental terms;",
        "keep payment proof;",
        "contact the rental business directly if something is unclear.",
        "For the full picture, read the Trust & Safety Policy.",
      ]},
      { h: "Your booking details", body: [
        "Private booking details are not the same as public rental listings. Public business information and private booking records are kept separate. Where required, ConfirmPH uses access or verification steps: such as booking information or a one-time code, before private details are shown.",
      ]},
      { h: "Who is behind ConfirmPH", body: [
        "ConfirmPH is being built specifically around real Philippine rental workflows, where customers may contact businesses through Messenger, Facebook, WhatsApp, Viber, phone, or in person. Our goal is simple: make important rental information clearer for both sides.",
      ]},
      { h: "Can I use ConfirmPH if I booked through Messenger or Facebook?", body: [
        "Yes. The original conversation can still happen there. If the rental business uses ConfirmPH, it can create or share a ConfirmPH booking record afterward.",
      ]},
      { h: "Does ConfirmPH guarantee my rental?", body: [
        "No. ConfirmPH records and communicates booking information. It does not operate the rental business or guarantee its performance.",
      ]},
      { h: "Do I need an account?", body: [
        "Many renter actions can be completed without a full account, although security checks such as booking details or OTP verification may be required where necessary to protect private information.",
      ]},
      { h: "A rental should start with clarity.", body: [
        "Whether you found the business through Facebook, Messenger, a recommendation, or ConfirmPH, you should know what was booked before you depend on it. That's what ConfirmPH is here to make easier.",
      ]},
    ],
  },
  tl: {
    hero: {
      kicker: "Tungkol sa ConfirmPH",
      title: "Madalas nagsisimula sa chat ang rental. Binibigyan ng ConfirmPH ang booking ng malinaw na record.",
      sub: "Sa Pilipinas, maraming rental ang nagsisimula sa Messenger, Facebook, WhatsApp, Viber, phone call, o walk-in. Normal lang iyon. Nagsisimula ang problema kapag ang importanteng details ay naiwan at nakakalat sa iba't ibang conversations. Tinutulungan ng ConfirmPH ang renter at rental business na magkaroon ng isang mas malinaw na record ng vehicle, dates, presyo, pickup details, at confirmation pagkatapos ng usapan. Hindi pagmamay-ari o pinapatakbo ng ConfirmPH ang mga rental business na makikita sa platform.",
    },
    sections: [
      { h: "Bakit ginawa ang ConfirmPH", body: [
        "Kahit may “yes, confirmed,” puwede pa ring may importanteng details na hindi malinaw.",
        "Anong vehicle? Ang eksaktong vehicle o malinaw na napagkasunduang category;",
        "Anong dates? Pickup at return date at oras;",
        "Magkano? Rental price, anumang advance o downpayment, anumang refundable security deposit, at ang natitirang rental balance;",
        "Saan? Pickup location at importanteng contact details.",
        "Dapat madaling makita ang mga sagot na ito sa isang malinaw na booking. Maaaring may isang version ang renter sa Messenger. Maaaring may ibang details na naisulat ang staff sa ibang lugar. At maaaring hindi nakita ng ibang employee ang original conversation. Ginawa ang ConfirmPH para may mas malinaw na record na puwedeng tingnan ng parehong sides.",
      ]},
      { h: "Ano talaga ang ginagawa ng ConfirmPH", body: [
        "I-record. Ire-record ng rental business ang vehicle, dates, service mode, presyo, anumang advance at security deposit, pickup, at iba pang importanteng booking details.",
        "I-review. Makikita ng renter kung ano ang ni-record ng business at puwede niya itong i-confirm o humingi ng correction kung may mali.",
        "I-follow. Mas madaling makita at balikan ang importanteng booking status, changes, at relevant updates habang nagpapatuloy ang rental: sa readiness at handover, return at inspection, at anumang settlement hanggang ma-close ang rental.",
        "Mag-book gaya ng dati. Mag-confirm nang malinaw.",
      ]},
      { h: "Ano talaga ang ibig sabihin ng “Confirmed”?", body: [
        "Ang “Confirmed” ay nangangahulugang ni-review at tinanggap ng renter ang partikular na booking version at material commercial terms na ipinakita noong oras na iyon. Kung may babaguhin ang business na material term pagkatapos, hindi nito mamanahin ang naunang confirmation: hihilingin sa renter na tanggapin ang bagong version, at nananatili sa history ang nauna.",
        "Kayang i-record ng ConfirmPH kung ano ang inilagay ng rental business;",
        "kung ano ang ni-review ng customer;",
        "ang confirmation ng customer;",
        "relevant recorded changes at booking status.",
        "Hindi ginagarantiya ng ConfirmPH na siguradong magiging available ang vehicle;",
        "na magiging perfect ang service ng rental business;",
        "ang condition ng vehicle, insurance coverage, o legal compliance;",
        "ang legal readiness ng business o vehicle, o ang eligibility ng driver;",
        "kung sino ang may kasalanan sa aksidente, o kung sino ang liable;",
        "refunds, o ang pagbabalik o entitlement sa security deposit.",
        "Binibigyan ng ConfirmPH ang parehong sides ng mas malinaw na shared record. Ang rental business pa rin ang responsable sa mismong rental.",
      ]},
      { h: "Ano ang puwedeng gawin ng renter sa ConfirmPH?", body: [
        "May booking ka na? Gamitin ang Hanapin ang booking ko para tingnan ang booking information na ni-record para sa iyo ng participating rental business.",
        "Nag-book ka sa labas ng ConfirmPH? Gamitin ang Humingi ng confirmation para hilingin sa rental business na gumawa o mag-share ng ConfirmPH booking record: Messenger, Facebook, WhatsApp, Viber, phone, o personal man ang pinagbukingan mo.",
        "Naghahanap ka pa? Gamitin ang Maghanap ng rentals para tingnan ang participating rental businesses at kanilang listed vehicles.",
        "Gusto mong mas prepared? Gamitin ang libreng renter guides at safety checklist bago magbayad, bago ang pickup, at bago mag-return.",
        "Ikaw ang magmamaneho? Tingnan kung sinong authorized drivers ang nasa booking, i-review ang anumang material change bago ito tanggapin, at sundan ang handover, return at post-rental status sa parehong record.",
      ]},
      { h: "Built para sa paraan ng pagre-rental sa Pilipinas", body: [
        "Hindi mo kailangang baguhin kung paano ka nakikipag-usap sa rental business.",
        "Messenger;",
        "Facebook;",
        "WhatsApp;",
        "Viber;",
        "phone;",
        "walk-in.",
        "Puwede pa ring doon magsimula ang usapan. Nagdadagdag lang ang ConfirmPH ng mas malinaw na structure pagkatapos magsimula ang conversation. Naiintindihan ng product na ito kung paano talaga gumagana ang rentals dito: hindi hinihingi na itigil ang paggamit ng Messenger.",
      ]},
      { h: "Mahuli ang misunderstanding bago ang pickup", body: [
        "Mas madaling ayusin ang problema bago ka pa bumiyahe.",
        "Maling date?",
        "Ibang vehicle?",
        "Hindi malinaw ang pickup location?",
        "Iba ang presyong na-record?",
        "Hindi tugma ang advance o security deposit?",
        "Mas madaling linawin ang mga iyon bago ang pickup kaysa kapag nandun ka na. Bago ang pickup, i-check ang ConfirmPH.",
      ]},
      { h: "Ano ang ConfirmPH, at ano ang hindi nito ginagawa", body: [
        "Isang software at information platform na tumutulong sa rental businesses at renters na gumawa at mag-reference ng mas malinaw na rental records ang ConfirmPH.",
        "Hindi ang rental business mismo ang ConfirmPH. Hindi pagmamay-ari ng ConfirmPH ang vehicle, hindi nito pinapatakbo ang rental, at hindi nito ginagarantiya ang future performance ng rental business.",
        "Importante pa rin ang sarili mong checks: i-inspect ang vehicle;",
        "basahin ang rental terms;",
        "itago ang payment proof;",
        "direktang kontakin ang rental business kapag may hindi malinaw.",
        "Para sa buong picture, basahin ang Trust & Safety Policy.",
      ]},
      { h: "Ang booking details mo", body: [
        "Magkaiba ang public rental listings at private booking records. Hiwalay ang public business information at private booking details. Kung kinakailangan, gumagamit ang ConfirmPH ng access at verification steps: gaya ng booking information o one-time code: bago ipakita ang private booking details.",
      ]},
      { h: "Sino ang nasa likod ng ConfirmPH", body: [
        "Ginagawa ang ConfirmPH base sa actual Philippine rental workflows, kung saan normal na makipag-usap ang customers sa businesses sa Messenger, Facebook, WhatsApp, Viber, phone, o personal. Simple ang goal namin: gawing mas malinaw ang importanteng rental information para sa parehong sides.",
      ]},
      { h: "Puwede ko bang gamitin ang ConfirmPH kung sa Messenger o Facebook ako nag-book?", body: [
        "Oo. Puwede pa ring doon mangyari ang original conversation. Kung gumagamit ng ConfirmPH ang rental business, maaari silang gumawa o mag-share ng ConfirmPH booking record pagkatapos.",
      ]},
      { h: "Ginagarantiya ba ng ConfirmPH ang rental ko?", body: [
        "Hindi. Nire-record at ipinapakita ng ConfirmPH ang booking information, pero hindi nito pinapatakbo ang rental business at hindi nito ginagarantiya ang kanilang performance.",
      ]},
      { h: "Kailangan ko ba ng account?", body: [
        "Maraming renter actions ang puwedeng gawin nang walang full account, pero maaaring kailanganin ang security checks gaya ng booking details o OTP para maprotektahan ang private information.",
      ]},
      { h: "Dapat malinaw ang rental bago ka umasa rito.", body: [
        "Kung nakita mo man ang rental business sa Facebook, Messenger, recommendation, o ConfirmPH, dapat alam mo kung ano talaga ang na-book bago ka magbayad o bumiyahe dahil dito. Iyan ang gustong gawing mas madali ng ConfirmPH.",
      ]},
    ],
  },
};

export const ABOUT_BUSINESS = {
  en: {
    hero: {
      kicker: "About ConfirmPH for Businesses",
      title: "Your customers can keep using Messenger. Your rental business shouldn't have to run from it.",
      sub: "ConfirmPH is built for Philippine rental businesses that receive customers through Facebook, Messenger, WhatsApp, Viber, calls, walk-ins, referrals, and repeat customers. Once a conversation becomes a real booking, give the vehicle, customer, staff activity, and history one proper place to live. Keep Messenger · Keep Facebook · Keep your existing customer channels.",
    },
    sections: [
      { h: "Why ConfirmPH exists", body: [
        "The problem is usually not getting the customer. It is keeping the rental organized after they message you.",
        "A customer messages Facebook. One staff member replies. The booking lands in a notebook or a sheet. A payment or security-deposit screenshot goes to another phone. The vehicle's information is somewhere else. The owner has to ask what's happening. Eventually, one detail gets missed.",
        "The problem is not necessarily careless staff: different people may simply be working from different information. ConfirmPH gives the rental one shared record.",
      ]},
      { h: "One rental. One record.", body: [
        "Your team should be able to answer these immediately.",
        "Who is the customer?",
        "Which vehicle?",
        "When is it booked?",
        "What rental price, advance, and security deposit were agreed?",
        "Who is driving: self-drive with an authorized driver, or one of your drivers?",
        "What needs attention next?",
        "If answering means searching Messenger, checking a notebook, and asking another staff member, the information is still scattered.",
      ]},
      { h: "ConfirmPH is the operating record behind the rental", body: [
        "Keep bookings clear: customer, vehicle, dates, price, deposit, and confirmation together;",
        "keep vehicles ready: bookings, availability, inspections, damage, incidents, and maintenance stay connected;",
        "keep staff aligned: staff work from the same information, reminders, and recorded changes;",
        "keep drivers ready: company drivers, availability, qualification and dispatch, and authorized self-drive drivers;",
        "keep legal readiness in view: business and vehicle permits, registration and insurance evidence with expiry dates;",
        "keep the financial record: payments, advances, security deposits and refunds, with Finance & Books where enabled;",
        "keep the history: important actions, confirmations, incidents, and changes remain referenceable later.",
      ]},
      { h: "Keep what already works", body: [
        "We are not asking you to stop using Messenger.",
        "Facebook / Messenger: talk to customers;",
        "WhatsApp / Viber: keep communicating;",
        "phone / walk-in: still works.",
        "ConfirmPH records and manages the rental after the conversation becomes a real booking. Message anywhere. Run the rental in ConfirmPH.",
      ]},
      { h: "For the owner", body: [
        "Know what's happening without asking everyone first.",
        "What's going out today?",
        "What's coming back?",
        "Which customer has not confirmed?",
        "Which vehicle has a problem?",
        "What changed?",
        "ConfirmPH helps turn business memory into business records.",
      ]},
      { h: "“Maliit pa kami.”", body: [
        "You do not need 50 vehicles to need a system.",
        "With only a few vehicles, an owner can often remember most bookings personally. As bookings, pickups, returns, staff, and vehicle issues increase, memory becomes less reliable. ConfirmPH lets the business build the habit earlier.",
        "You do not need to become a big rental company before you start running like one.",
      ]},
      { h: "Customers notice when a rental business is organized", body: [
        "A clear booking reference;",
        "written vehicle and date details;",
        "a confirmation link;",
        "clear pickup information;",
        "proper condition/history records;",
        "booking-linked reviews.",
        "ConfirmPH helps a small rental business operate more professionally without pretending to be a large company. You can be small and still run professionally.",
      ]},
      { h: "Hindi kailangang maging techy", body: [
        "ConfirmPH follows work rental businesses already do: add the booking;",
        "select the vehicle;",
        "record the customer;",
        "send confirmation;",
        "handle pickup;",
        "handle return;",
        "record what happens in between.",
        "Start with 5 bookings. See whether the information becomes easier to find and the team becomes easier to coordinate.",
      ]},
      { h: "Built around the whole rental, not just the reservation", body: [
        "A rental is not finished when somebody puts a date on a calendar. ConfirmPH is designed around what happens before, during, and after the booking:",
        "Inquiry → Booking and agreement → Confirmation → Readiness → Handover → Active rental → Return and inspection → Settlement and claims if needed → Closure → Review.",
      ]},
      { h: "The business tools, grouped", body: [
        "Bookings & customers: bookings, agreement versions, confirmations, inquiries, authorized drivers, payments, advances and security deposits, balances, customer history;",
        "fleet & readiness: vehicles, calendar, inspections, damage, maintenance, incidents, Vehicle Legal Readiness and Enhanced Vehicle Safety;",
        "staff & operations: permissions, Driver Profiles, availability and dispatch, reminders, activity, notes, and follow-up;",
        "business & finance: Business Legal Readiness and Finance & Books;",
        "growth & visibility: public profile, Discover Rentals, reviews, QR, mini-site, and website tools.",
      ]},
      { h: "Better operations, easier to trust", body: [
        "Depending on plan and settings, ConfirmPH may support a public profile;",
        "rental listings;",
        "booking-linked reviews;",
        "website and visibility tools.",
        "The goal is not just to make the business look professional. The goal is to help the experience behind that appearance become professional too.",
      ]},
      { h: "Practical training for owners and staff", body: [
        "ConfirmPH includes short scenario-based guides and exercises covering situations such as booking mistakes;",
        "late returns;",
        "damage disputes;",
        "security deposits;",
        "complaints;",
        "vehicle readiness.",
        "Practice the situation before your staff faces it with a real customer.",
      ]},
      { h: "Who is behind ConfirmPH", body: [
        "ConfirmPH is being built around real Philippine rental operations, where customers contact businesses through Messenger, Facebook, WhatsApp, Viber, phone, or in person.",
      ]},
      { h: "Your business stays in control", body: [
        "ConfirmPH is software. We do not own your vehicles;",
        "employ your staff;",
        "set your prices;",
        "operate your rental business.",
        "Your business remains responsible for vehicle condition, contracts, payments, insurance, documents, customer service, and legal compliance. ConfirmPH helps keep the information and workflow around those responsibilities more organized, and its Legal Readiness tools can track and readiness-check some of them, but that is not government approval and does not make the business compliant. For the full picture, read the Business Terms and Trust & Safety Policy.",
      ]},
      { h: "Don't change your whole business. Put your next booking in ConfirmPH.", body: [
        "Keep Messenger. Keep taking calls. Keep serving customers the way you already do. Just give the next rental one proper record and see whether the business becomes easier to control.",
        "30 days free · No credit card needed. Message anywhere. Run the rental in ConfirmPH.",
      ]},
    ],
  },
  tl: {
    hero: {
      kicker: "Tungkol sa ConfirmPH para sa mga Rental Business",
      title: "Puwedeng manatili sa Messenger ang customers mo. Pero hindi kailangang sa Messenger din umiikot ang buong rental business mo.",
      sub: "Ang ConfirmPH ay ginawa para sa mga rental business sa Pilipinas na tumatanggap ng customers mula sa Facebook, Messenger, WhatsApp, Viber, tawag, walk-in, referrals, at repeat customers. Kapag naging totoong booking na ang usapan, puwedeng ilagay sa isang maayos na sistema ang booking, sasakyan, customer, staff activity, at history nito. Keep Messenger · Keep Facebook · Keep your existing customer channels.",
    },
    sections: [
      { h: "Bakit ginawa ang ConfirmPH?", body: [
        "Kadalasan, hindi problema ang paghahanap ng customer. Ang mahirap ay panatilihing maayos ang rental pagkatapos nilang mag-message.",
        "May customer na nag-message sa Facebook. May staff na sumagot. Nailagay ang booking sa notebook o spreadsheet. Nasa ibang phone ang screenshot ng payment o security deposit. Nasa ibang lugar naman ang impormasyon tungkol sa sasakyan. Kapag gustong malaman ng owner kung ano na ang status, kailangan pang magtanong sa staff. Hanggang sa may isang detalye na makalimutan o mawala.",
        "Hindi ibig sabihin nito na pabaya ang staff: madalas, magkakaiba lang ang impormasyong tinitingnan ng bawat tao. Binibigyan ng ConfirmPH ang bawat rental ng isang shared record.",
      ]},
      { h: "Isang rental. Isang record.", body: [
        "Dapat kayang sagutin agad ng team mo ang mga ito.",
        "Sino ang customer?",
        "Anong sasakyan ang naka-book?",
        "Kailan ang pickup at return?",
        "Magkano ang napagkasunduang rental price, advance, at security deposit?",
        "Sino ang magmamaneho: self-drive na may authorized driver, o isa sa mga driver mo?",
        "Ano ang kailangang asikasuhin next?",
        "Kung kailangan pang maghanap sa Messenger, tumingin sa notebook, at magtanong sa ibang staff para masagot ang mga ito, kalat pa rin ang impormasyon.",
      ]},
      { h: "Ang ConfirmPH ang operating record sa likod ng rental", body: [
        "Panatilihing malinaw ang bookings: customer, sasakyan, dates, presyo, deposit, at confirmation, magkakasama sa isang lugar;",
        "panatilihing handa ang mga sasakyan: bookings, availability, inspections, damage, incidents, at maintenance ay magkakaugnay;",
        "panatilihing aligned ang staff: pare-parehong impormasyon ang nakikita ng staff, kasama ang reminders at recorded changes;",
        "panatilihing handa ang drivers: company drivers, availability, qualification at dispatch, at authorized self-drive drivers;",
        "panatilihing nakikita ang legal readiness: business at vehicle permits, registration at insurance evidence kasama ang expiry dates;",
        "panatilihin ang financial record: payments, advances, security deposits at refunds, kasama ang Finance & Books kung naka-enable;",
        "panatilihin ang history: mahalagang actions, confirmations, incidents, at pagbabago ay may record na puwedeng balikan.",
      ]},
      { h: "Ituloy ang gumagana na", body: [
        "Hindi namin hinihinging tigilan mo ang Messenger.",
        "Facebook / Messenger: doon ka pa rin puwedeng makipag-usap sa customers;",
        "WhatsApp / Viber: puwede mong ituloy ang communication doon;",
        "tawag / walk-in: walang kailangang baguhin.",
        "Dito nire-record at mina-manage ang rental kapag naging totoong booking na ang usapan. Kahit saan manggaling ang message, sa ConfirmPH nakaayos ang rental.",
      ]},
      { h: "Para sa owner", body: [
        "Alamin kung ano ang nangyayari nang hindi kailangang tanungin muna ang lahat.",
        "Ano ang lalabas ngayong araw?",
        "Ano ang babalik?",
        "Sinong customer ang hindi pa nagko-confirm?",
        "Aling sasakyan ang may problema?",
        "Ano ang nabago sa booking?",
        "Tinutulungan ng ConfirmPH na gawing business record ang dating nasa memorya lang.",
      ]},
      { h: "“Maliit pa kami.”", body: [
        "Hindi mo kailangang magkaroon ng 50 sasakyan bago mo kailanganin ang maayos na sistema.",
        "Kapag kaunti pa ang vehicles, minsan kayang tandaan ng owner ang halos lahat ng bookings. Pero habang dumarami ang bookings, pickups, returns, staff, at vehicle issues, mas mahirap umasa sa memorya lang. Puwedeng simulan ang magandang sistema habang maliit pa ang business.",
        "Hindi mo kailangang maging malaking rental company bago ka magsimulang mag-operate nang maayos.",
      ]},
      { h: "Napapansin ng customers kapag organisado ang rental business", body: [
        "Malinaw na booking reference;",
        "written vehicle at date details;",
        "confirmation link;",
        "malinaw na pickup information;",
        "maayos na condition/history records;",
        "booking-linked reviews.",
        "Tinutulungan ng ConfirmPH ang maliit na rental business na mag-operate nang mas professional nang hindi kailangang magkunwaring malaking kumpanya. Puwede kang maliit at professional pa rin.",
      ]},
      { h: "Hindi kailangang maging techy", body: [
        "Sinusundan lang ng ConfirmPH ang mga trabahong ginagawa na ng rental business: idagdag ang booking;",
        "piliin ang sasakyan;",
        "ilagay ang customer;",
        "ipadala ang confirmation;",
        "asikasuhin ang pickup;",
        "asikasuhin ang return;",
        "i-record ang nangyari sa pagitan.",
        "Magsimula sa 5 bookings. Tingnan kung mas madali nang hanapin ang impormasyon at mas madali nang mag-coordinate ang team.",
      ]},
      { h: "Ginawa para sa buong rental, hindi lang sa reservation", body: [
        "Hindi natatapos ang rental dahil lang nailagay na ang date sa calendar. Ang ConfirmPH ay ginawa para sa mga nangyayari bago, habang, at pagkatapos ng booking:",
        "Inquiry → Booking at agreement → Confirmation → Readiness → Handover → Active rental → Return at inspection → Settlement at claims kung kailangan → Closure → Review.",
      ]},
      { h: "Mga business tools sa isang lugar", body: [
        "Bookings at customers: bookings, agreement versions, confirmations, inquiries, authorized drivers, payments, advances at security deposits, balances, at customer history;",
        "fleet at readiness: vehicles, calendar, inspections, damage, maintenance, incidents, Vehicle Legal Readiness at Enhanced Vehicle Safety;",
        "staff at operations: permissions, Driver Profiles, availability at dispatch, reminders, activity, notes, at follow-up;",
        "business at finance: Business Legal Readiness at Finance & Books;",
        "growth at visibility: public profile, Discover Rentals, reviews, QR, mini-site, at website tools.",
      ]},
      { h: "Mas maayos na operasyon, mas madaling pagkatiwalaan", body: [
        "Depende sa plan at settings, maaaring magkaroon ang business ng public rental profile;",
        "rental listings;",
        "booking-linked reviews;",
        "website at visibility tools.",
        "Ang goal ay hindi lang magmukhang professional. Ang mas mahalaga ay gawing mas professional ang actual experience sa likod nito.",
      ]},
      { h: "Practical training para sa owner at staff", body: [
        "May maiikling scenario-based guides at exercises ang ConfirmPH tungkol sa mga sitwasyong maaaring mangyari sa totoong rental business, tulad ng booking mistakes;",
        "late returns;",
        "damage disputes;",
        "security deposits;",
        "complaints;",
        "vehicle readiness.",
        "Practice muna bago mangyari sa totoong customer.",
      ]},
      { h: "Sino ang nasa likod ng ConfirmPH?", body: [
        "Ginawa ang ConfirmPH para sa tunay na rental operations sa Pilipinas, kung saan normal na makipag-usap ang customers sa businesses sa Messenger, Facebook, WhatsApp, Viber, phone, o personal.",
      ]},
      { h: "Kontrolado mo pa rin ang business mo", body: [
        "Ang ConfirmPH ay software. Hindi namin pagmamay-ari ang sasakyan mo;",
        "empleyado ang staff mo;",
        "itinatakda ang presyo mo;",
        "pinapatakbo ang rental business mo.",
        "Ang rental business mo pa rin ang responsable sa condition ng sasakyan, contracts, payments, insurance, documents, customer service, at legal compliance. Ang trabaho ng ConfirmPH ay tulungan kang panatilihing mas maayos ang impormasyon at workflow sa paligid ng mga responsibilidad na iyon, at kaya ng Legal Readiness tools nito na i-track at i-readiness-check ang ilan sa mga ito, pero hindi ito government approval at hindi nito ginagawang compliant ang business. Para sa buong picture, basahin ang Business Terms at Trust & Safety Policy.",
      ]},
      { h: "Hindi mo kailangang baguhin agad ang buong business mo. Ilagay mo muna ang susunod mong booking sa ConfirmPH.", body: [
        "Ituloy mo ang Messenger. Ituloy mo ang tawag. Ituloy mo ang paraan kung paano ka kumukuha at nag-aasikaso ng customers. Bigyan mo lang ang susunod na rental ng isang maayos na record at tingnan kung mas madaling kontrolin ang business.",
        "30 days free · Walang card na kailangan. Kahit saan manggaling ang message, sa ConfirmPH nakaayos ang rental.",
      ]},
    ],
  },
};
